import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { useTextStyle } from '@webapp/common/components/custom-styles';
import { HelpText } from '@webapp/common/components/help-text';
import { Required } from '@webapp/common/components/misc';
import { CssPageBlock, CssUiComponent } from '@webapp/common/components/survey-custom';
import { Textfield } from '@webapp/common/components/textfield';
import { getColor, mapQuestionBlockPositionToAlignItems, SubstrateType } from '@webapp/common/lib/ui';
import { striptags } from '@webapp/common/lib/utils';
import type { PageModel } from '@webapp/common/resources/entities';
import { TestAnswerReactingState } from '@webapp/common/resources/entities/question';
import { isOrderVisible } from '@webapp/common/resources/question-types';
import { QuestionType } from '@webapp/common/resources/survey';

import { AdditionalContent } from '@webapp/survey/src/components/questions/components/additional-content';
import { TestAnswerModal } from '@webapp/survey/src/components/test-answer-modal/test-answer-modal';
import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';
import { selectInfo, selectIsWithAudioRecogn } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { invalidMessageFactory, styleToQuestionPosition } from '../lib';
import css from '../question.css';

import { Header } from './header';
import { TypeView } from './type-view';

const Comment: FC<{
    commentRequired: boolean;
    commentInvalid: boolean;
    comment: string;
    onChange: (v: string) => void;
    commentTextHelp: string;
    mark: ReactNode;
}> = ({ comment, commentInvalid, commentRequired, commentTextHelp, mark, onChange }) => {
    const { textStyle } = useTextStyle();
    const isWithAudioRecogn = useStateSelector(selectIsWithAudioRecogn);

    return (
        <div
            style={textStyle}
            className={cn(css.comment, {
                [css.commentRequired]: commentRequired,
                [css.rowInvalid]: commentInvalid,
            })}
        >
            <Textfield
                withSpeechRecognition={isWithAudioRecogn}
                placeholder={commentTextHelp || ''}
                style={textStyle}
                className={cn({ [css.commentInvalid]: commentInvalid })}
                value={comment || ''}
                onChange={onChange}
            />
            {mark}
        </div>
    );
};

const InvalidMessage: FC<{
    msg: string;
}> = ({ msg }) => <div className={cn(CssUiComponent.INVALID, css.requiredText)}>{msg}</div>;

const CommentInvalidMessage: FC<{
    msg: string;
}> = ({ msg }) => <div className={cn(CssUiComponent.INVALID, css.commentInvalid)}>{msg}</div>;

const RequiredMessage: FC<{
    msg: string;
}> = ({ msg }) => <div className={cn(CssUiComponent.REQUIRED, css.requiredText)}>{msg}</div>;

export const QuestionView = forwardRef<
    HTMLDivElement,
    {
        question: QuestionActorRef;
        page: PageModel;
        order?: number;
        offset?: number;
        readOnly?: boolean;
        twisterReadOnly?: boolean;
        showHeader?: boolean;
        substrateStyles: object;
    }
>(({ page, question, readOnly, twisterReadOnly, showHeader, substrateStyles, offset }, refSelf) => {
    const context = useSelector(question, (actor) => {
        return actor.context;
    });
    const {
        commentInvalid,
        invalid,
        quotaError,
        title,
        params: {
            comment: commentEnabled,
            commentRequired,
            commentText: commentTextHelp,
            help,
            helpText,
            required,
            testAnswerCorrectText,
            testAnswerIncorrectText,
        },
        type,
        testAnswerReactingState,
    } = context;

    const response = useSelector(question, (r) => r.context.responseActor);
    const comment = useSelector(response, (r) => r.context.comment);

    const info = useStateSelector(selectInfo);
    const {
        design: {
            fonts: { elementColor },
            view: { questionBlockPosition },
            brand: { substrate },
        },
        params: {
            alert,
            other: { disallowTextCopy, showQuestionNumber, showRequiredToQuestion },
        },
    } = info;
    const invalidMessage = invalid && invalidMessageFactory(alert, context);
    const questionPositionClass = questionBlockPosition ? styleToQuestionPosition[questionBlockPosition] : '';
    const adjustedShowHeader = !readOnly || showHeader;
    const showCommentAlert = !readOnly && commentEnabled && commentRequired && commentInvalid;
    const showRequired = !readOnly && invalid && required;
    const showQuotaAlert = !readOnly && quotaError;
    const withError = invalid || commentInvalid || quotaError;
    const showRequiredMark = required && showRequiredToQuestion;
    const showOrder = Boolean(showQuestionNumber && isOrderVisible(type) && !page.random);
    const showCommentRequiredMark = commentRequired && showRequiredToQuestion;
    const markColor = withError ? getColor('red') : elementColor;
    const commentMarkColor = commentInvalid ? getColor('red') : elementColor;
    const mark = showRequiredMark ? <Required className={css.requiredMark} color={markColor} /> : null;
    const commentMark = showCommentRequiredMark ? (
        <Required className={css.commentRequiredMark} color={commentMarkColor} />
    ) : null;
    const { itemsGap } = useTextStyle();
    const isCSI = type === QuestionType.CSI;
    const isKano = type === QuestionType.KANO;

    const viewStyle: CSSProperties = useMemo(() => {
        let styles = {
            rowGap: itemsGap,
            alignItems: mapQuestionBlockPositionToAlignItems[questionBlockPosition],
        };

        if (substrate === SubstrateType.EVERY_QUESTION) {
            styles = { ...styles, ...substrateStyles };
        }

        return styles;
    }, [itemsGap, questionBlockPosition, substrate]);

    useEffect(() => {
        if ((isCSI || isKano) && commentRequired) {
            context.responseActor.send({ type: 'CHANGE_COMMENT', value: '' });
        }
    }, [commentRequired, context.responseActor, isCSI, isKano]);

    const changeComment = useCallback(
        (value: string) => {
            context.responseActor.send({ type: 'CHANGE_COMMENT', value });
        },
        [context],
    );

    useLayoutEffect(() => {
        question.send({ type: 'SHOW', readOnly: readOnly || twisterReadOnly });
    }, [question, readOnly, twisterReadOnly]);

    return (
        <div
            data-testid={striptags(title)}
            ref={refSelf}
            style={viewStyle}
            className={cn(
                CssPageBlock.QUESTION,
                `${CssPageBlock.QUESTION}_${context.id}`,
                `${CssPageBlock.QUESTION}_type_${context.type}`,
                css.question,
                {
                    [CssUiComponent.REQUIRED]: showRequired,
                    [CssUiComponent.INVALID]: invalid || quotaError,
                    [css.withError]: withError,
                    [css.noSelection]: disallowTextCopy,
                    [css.disabled]: readOnly,
                },
            )}
        >
            {adjustedShowHeader && (
                <Header
                    info={info}
                    mark={mark}
                    question={context}
                    readOnly={readOnly}
                    twisterReadOnly={twisterReadOnly}
                    showOrder={showOrder}
                    showRequired={showRequiredMark}
                    withError={withError}
                />
            )}
            {showRequired && <RequiredMessage msg={alert.required} />}
            {showCommentAlert && <CommentInvalidMessage msg={alert.comment} />}
            {showQuotaAlert && <InvalidMessage msg={alert.quotaAnswersHideText} />}
            {invalidMessage && <InvalidMessage msg={invalidMessage} />}
            <AdditionalContent question={context} offset={offset} />
            {Boolean(help && helpText) && <HelpText className={css.help} value={helpText} />}
            <div className={cn(css.contentWrap)}>
                <div
                    className={cn(css.content, questionPositionClass, {
                        [css.readOnly]: readOnly,
                    })}
                >
                    <TypeView
                        twisterReadOnly={twisterReadOnly}
                        actor={question}
                        readOnly={readOnly}
                        question={context}
                        questionBlockPosition={questionBlockPosition}
                    />
                </div>
            </div>
            {commentEnabled && !isCSI && !isKano && (
                <Comment
                    comment={comment}
                    commentInvalid={commentInvalid}
                    commentRequired={commentRequired}
                    commentTextHelp={commentTextHelp}
                    mark={commentMark}
                    onChange={changeComment}
                />
            )}
            {(testAnswerReactingState === TestAnswerReactingState.CORRECT ||
                testAnswerReactingState === TestAnswerReactingState.INCORRECT) && (
                <TestAnswerModal
                    testAnswerCorrectText={testAnswerCorrectText}
                    testAnswerIncorrectText={testAnswerIncorrectText}
                    correct={testAnswerReactingState === TestAnswerReactingState.CORRECT}
                    onClose={() => {
                        question.send({ type: 'HIDE_TEST_ANSWER_REACTING_NOTIFICATION' });
                    }}
                />
            )}
        </div>
    );
});
QuestionView.displayName = 'QuestionView';
